import {createTheme, CssBaseline, ThemeProvider} from "@mui/material";
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import {ThemeContext} from "./ThemeContext";
import {getLocalStorageItem, setLocalStorageItem} from "utils/storage";

export const ThemeModeProvider: FC<PropsWithChildren> = ({children}) => {
  const [darkMode, setDarkMode] = useState(
    () => getLocalStorageItem("theme-mode") === "dark",
  );

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      setLocalStorageItem("theme-mode", next ? "dark" : "light");
      return next;
    });
  }, []);

  // Smooth transitions for background and text
  useEffect(() => {
    document.body.style.transition =
      "background-color 0.4s ease, color 0.4s ease";
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "#121212" : "#f0f0f0",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
          text: {
            primary: darkMode ? "#ffffff" : "#333333",
          },
        },
        typography: {
          fontFamily: "'Roboto Flex', sans-serif",
        },
        cssVariables: true,
      }),
    [darkMode],
  );

  const contextValue = useMemo(
    () => ({
      darkMode,
      toggleDarkMode,
    }),
    [darkMode, toggleDarkMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
